import React, { useEffect, useState } from "react";
import { FaPrint, FaDownload } from "react-icons/fa";
import "../styles/loanCalculator.css";
import carImg from "../images/Car-Images/matel-demio.png"; // replace with your car image
import RelatedCarFinancing from "../components/RelatedCarFinancing";
import LoanApplication from "../components/LoanApplication";
import { getAProduct, getRelatedProducts } from "../features/products/productSlice";
import { useMediaQuery } from "react-responsive";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import LoanFinancingBreadCrumb from "../components/LoanFinancingBreadcrumb";
import loader from "../images/loader/loader1.jpg"
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";

const LoanCalculator = () => {
  const location = useLocation();
  const getProductSlug = location.pathname.split("/")[2];
  const [loanType, setLoanType] = useState("salaried");
  const [loanAmount, setLoanAmount] = useState(0);
  const [months, setMonths] = useState(60);
  const [interestRate, setInterestRate] = useState(18.7);
  const [schedule, setSchedule] = useState([]);
  const [deposit, setDeposit] = useState(0);

  const dispatch = useDispatch();
  const productState = useSelector((state) => state?.product?.singleproduct);
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const relatedProducts = useSelector((state) => state.product.relatedProducts);

  useEffect(() => {
    if (!productState) {
      dispatch(getAProduct(getProductSlug));
      dispatch(getRelatedProducts(getProductSlug));
    }
  }, [dispatch, getProductSlug, productState]);

// 🧮 Auto-calculate loan based on type and product price
useEffect(() => {
  if (!productState?.price) return;

  const price = productState.price;
  let dep, amount, monthsVal, rate;

  // NEW RULES
  rate = 20;       // 20% interest for both
  monthsVal = 24;  // 24 months repayment period

  if (loanType === "business") {
    dep = price * 0.30;   // 30% deposit
  } else {
    dep = price * 0.15;   // 15% deposit
  }

  amount = price - dep;

  setDeposit(dep);
  setLoanAmount(amount);
  setMonths(monthsVal);
  setInterestRate(rate);

  // 🔢 Auto-generate repayment schedule (EMI)
  const monthlyRate = rate / 100 / 12;
  const emi =
    (amount * monthlyRate * Math.pow(1 + monthlyRate, monthsVal)) /
    (Math.pow(1 + monthlyRate, monthsVal) - 1);

  let balance = amount;
  const repaymentSchedule = [];

  for (let i = 1; i <= monthsVal; i++) {
    const interestPayment = balance * monthlyRate;
    const principalPayment = emi - interestPayment;
    balance -= principalPayment;

    repaymentSchedule.push({
      month: i,
      payment: emi.toFixed(2),
      principal: principalPayment.toFixed(2),
      interest: interestPayment.toFixed(2),
      balance: balance > 0 ? balance.toFixed(2) : "0.00",
    });
  }

  setSchedule(repaymentSchedule);
}, [productState, loanType]);


  // 📆 Calculate EMI + Repayment Schedule
  const calculateLoan = () => {
    const monthlyRate = interestRate / 100 / 12;
    const emi =
      (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, months)) /
      (Math.pow(1 + monthlyRate, months) - 1);

    let balance = loanAmount;
    const repaymentSchedule = [];

    for (let i = 1; i <= months; i++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = emi - interestPayment;
      balance -= principalPayment;

      repaymentSchedule.push({
        month: i,
        payment: emi.toFixed(2),
        principal: principalPayment.toFixed(2),
        interest: interestPayment.toFixed(2),
        balance: balance > 0 ? balance.toFixed(2) : "0.00",
      });
    }

    setSchedule(repaymentSchedule);
  };

  const handlePrint = () => window.print();

  const handleDownload = () => {
    const data = [
      ["Month", "Payment", "Principal", "Interest", "Balance"],
      ...schedule.map((row) => [
        row.month,
        row.payment,
        row.principal,
        row.interest,
        row.balance,
      ]),
    ];
    const csvContent =
      "data:text/csv;charset=utf-8," + data.map((e) => e.join(",")).join("\n");
    const link = document.createElement("a");
    link.href = encodeURI(csvContent);
    link.download = "loan_schedule.csv";
    link.click();
  };

  return (
    <>
    <LoanFinancingBreadCrumb title={productState?.title}/>
    <div className="loan-calculator-container">
      {/* Car Section */}
      <div className="car-details">
         
       
         <LazyLoadImage
           alt={productState?.title}
           effect="blur"
           src={productState?.images?.[0]?.url || loader}
           placeholderSrc={loader}
           width={isDesktop ? "300px" : "100%"}
           height="100%"
           className="img-fluid mx-auto img-el"
           style={{
             objectFit: "cover",
             borderRadius: "8px",
             cursor: "pointer",
           }}
         />      
        <h2>{productState?.title}</h2>
        <p className="car-price">
          KES {productState?.price?.toLocaleString()}
        </p>
      </div>

      {/* Loan Type */}
      <div className="loan-type">
        <label>
          <input
            type="radio"
            value="salaried"
            checked={loanType === "salaried"}
            onChange={(e) => setLoanType(e.target.value)}
          />
          Salaried Loan (20%)
        </label>
        <label>
          <input
            type="radio"
            value="business"
            checked={loanType === "business"}
            onChange={(e) => setLoanType(e.target.value)}
          />
          Business Loan (20%)
        </label>
      </div>

      {/* Loan Form */}
      <div className="loan-form">
        <div className="form-group">
          <label>Loan Amount (KES)</label>
          <input type="number" value={loanAmount} readOnly />
        </div>
        <div className="form-group">
          <label>Term (Months)</label>
          <input type="number" value={months} readOnly />
        </div>
        <div className="form-group">
          <label>Interest Rate (%)</label>
          <input type="number" value={interestRate} readOnly />
        </div>

        <button className="calculate-btn" onClick={calculateLoan}>
          Calculate Loan
        </button>
      </div>

      {/* Deposit Display */}
      <div className="deposit">
        <p>
          Deposit: <span>KES {deposit.toLocaleString()}</span>
        </p>
      </div>

      {/* Repayment Table */}
      {schedule.length > 0 && (
        <div className="repayment-schedule">
          <h3>
            {loanType === "salaried"
              ? "Salaried Loan Plan"
              : "Business Loan Plan"}
          </h3>
          <div className="table__container">
         <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Payment</th>
                <th>Principal</th>
                <th>Interest</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((row, index) => (
                <tr key={index}>
                  <td>{row.month}</td>
                  <td>{row.payment}</td>
                  <td>{row.principal}</td>
                  <td>{row.interest}</td>
                  <td>{row.balance}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          

          {/* Actions */}
          <div className="actions">
            <button onClick={handlePrint} className="action-btn">
              <FaPrint /> Print
            </button>
            <button onClick={handleDownload} className="action-btn">
              <FaDownload /> Download
            </button>
          </div>
        </div>
      )}

      <LoanApplication slug={getProductSlug} title={productState?.title} />
      <RelatedCarFinancing />

      {/* Static Blog Section */}
     
<div className="loan-blog">
  <h2>How Our Car Loan Financing Works</h2>
  <p>
    Our car loan program is designed to make it easier for you to own your dream vehicle
    without paying the full amount upfront. Once you select your car, you can apply for
    financing under either a <strong>Salaried Loan</strong> or a <strong>Business Loan</strong>,
    depending on your income source.
  </p>

  <h3>1. Step-by-Step Loan Process</h3>
  <ul>
    <li><strong>Step 1:</strong> Choose your preferred car from our listings.</li>
    <li><strong>Step 2:</strong> Select your loan category — Salaried or Business.</li>
    <li><strong>Step 3:</strong> The system automatically calculates your
      <strong> required deposit </strong> and <strong> loan amount </strong> based on the car price.
    </li>
    <li><strong>Step 4:</strong> Fill and submit the loan application form.</li>
    <li><strong>Step 5:</strong> Our loan officer contacts you for verification and approval.</li>
    <li><strong>Step 6:</strong> Once approved, you pay the deposit and sign the loan agreement.</li>
    <li><strong>Step 7:</strong> You begin your <strong>24 monthly repayments</strong> as shown
      by the repayment calculator.
    </li>
  </ul>

  <h3>2. Understanding the Deposit</h3>
  <p>
    The deposit is a percentage of the car price paid upfront to activate financing.
    This reduces the total amount borrowed and your monthly installment.
  </p>
  <ul>
    <li><strong>Salaried Loan:</strong> Requires <strong>15% deposit</strong> of the vehicle price.</li>
    <li><strong>Business Loan:</strong> Requires <strong>30% deposit</strong> of the vehicle price.</li>
  </ul>

  <p>
    Example: For a vehicle priced at <strong>KES 1,000,000</strong>:
  </p>
  <ul>
    <li><strong>Salaried Loan Deposit:</strong> KES 150,000</li>
    <li><strong>Business Loan Deposit:</strong> KES 300,000</li>
  </ul>

  <h3>3. Loan Types Explained</h3>

  <h4>Salaried Loan</h4>
  <p>
    Ideal for individuals with a stable monthly income. This loan features:
  </p>
  <ul>
    <li><strong>15% deposit</strong></li>
    <li><strong>20% annual interest rate</strong></li>
    <li><strong>24-month repayment period</strong></li>
    <li>Affordable and fixed monthly installments</li>
  </ul>

  <h4>Business Loan</h4>
  <p>
    Best suited for business owners and self-employed individuals who prefer flexible
    financial planning. This option includes:
  </p>
  <ul>
    <li><strong>30% deposit</strong></li>
    <li><strong>20% annual interest rate</strong></li>
    <li><strong>24-month repayment period</strong></li>
    <li>Lower risk requirements due to higher deposit</li>
  </ul>

  <h3>4. How the Loan Is Calculated</h3>
  <p>
    The loan calculator uses the <strong>Equated Monthly Installment (EMI)</strong> method
    to compute your monthly repayments.
  </p>
  <p className="formula">
    EMI = [ P × r × (1 + r)<sup>n</sup> ] ÷ [ (1 + r)<sup>n</sup> − 1 ]
  </p>

  <p>
    Where: <br />
    <strong>P</strong> = Loan Amount (Car Price − Deposit) <br />
    <strong>r</strong> = Monthly Interest Rate (20% ÷ 12 ÷ 100) <br />
    <strong>n</strong> = 24 months (fixed repayment period) <br />
  </p>

  <p>
    Each EMI is split into <strong>Principal</strong> (the actual borrowed amount)
    and <strong>Interest</strong> (the cost of borrowing). Over time, the interest
    reduces while the principal increases until the loan is cleared.
  </p>

  <h3>5. Why Choose Our Financing</h3>
  <ul>
    <li>Flexible loan types for both salaried and business clients</li>
    <li>Affordable deposits (15% or 30%)</li>
    <li>Fast approval process</li>
    <li>Transparent 24-month repayment schedule</li>
    <li>No hidden charges or penalties</li>
  </ul>

  <p className="note">
    <strong>Note:</strong> Use the calculator above to estimate your monthly repayments
    based on the deposit and interest. Once ready, apply using the Loan Application
    form to begin your financing journey.
  </p>
</div>


    </div>
    </>
    
  );
};

export default LoanCalculator;
